import { concat, from, of } from 'rxjs';
import { v4 as uuid } from 'uuid';
import assert from '@banking/ui/utils/assert';
import { LinkBankAccountRequest } from '@square/dashboard-protos/models/squareup/deposits/bank_account_service';
import { htmlSafe } from '@ember/template';

// State
// =============================================================================

export let Screen = /*#__PURE__*/function (Screen) {
  Screen["CONFIRM"] = "CHANGE_BANK_ACCOUNT/CONFIRM";
  Screen["ERROR"] = "CHANGE_BANK_ACCOUNT/ERROR";
  Screen["LOADING"] = "CHANGE_BANK_ACCOUNT/LOADING";
  Screen["SUCCESS"] = "CHANGE_BANK_ACCOUNT/SUCCESS";
  return Screen;
}({});
export const INITIAL_STATE = {
  bankAccountToken: '',
  isChangeBankAccountSuccess: false,
  isDone: false,
  isModalOpen: false,
  screen: Screen.CONFIRM,
  selectedBankLabel: {
    bankAccountLabelHtmlSafe: htmlSafe(''),
    bankAccountLabel: ''
  },
  selectedLocationName: '',
  unitToken: ''
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});

// Action
// =============================================================================

export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["IncrementScreen"] = "CHANGE_BANK_ACCOUNT/INCREMENT_SCREEN";
  ActionType["ToggleChangeBankAccountModal"] = "CHANGE_BANK_ACCOUNT/TOGGLE_CHANGE_BANK_ACCOUNT_MODAL";
  ActionType["UpdateBankAccountReceived"] = "CHANGE_BANK_ACCOUNT/UPDATE_BANK_ACCOUNT_RECEIVED";
  ActionType["UpdateBankAccountRejected"] = "CHANGE_BANK_ACCOUNT/UPDATE_BANK_ACCOUNT_REJECTED";
  ActionType["UserClickUpdateBankAccount"] = "CHANGE_BANK_ACCOUNT/USER_CLICK_UPDATE_BANK_ACCOUNT";
  return ActionType;
}({});
// Action Creators
// =============================================================================

export const userClickUpdateBankAccountAction = () => ({
  type: ActionType.UserClickUpdateBankAccount
});
export const updateBankAccountRejectedAction = error => ({
  type: ActionType.UpdateBankAccountRejected,
  error
});
export const updateBankAccountReceivedAction = response => ({
  type: ActionType.UpdateBankAccountReceived,
  response
});
export const incrementScreenAction = screen => ({
  type: ActionType.IncrementScreen,
  screen
});
export const toggleChangeBankAccountModalAction = (unitToken, selectedBankLabel, selectedLocationName, bankAccountToken) => ({
  type: ActionType.ToggleChangeBankAccountModal,
  unitToken,
  selectedBankLabel,
  selectedLocationName,
  bankAccountToken
});

// Update
// =============================================================================

function handleUserClickUpdateBankAccount(context, state) {
  const {
    unitToken,
    bankAccountToken
  } = state;
  const work = () => {
    const swapBankAccount = context.bankAccountApi.linkBankAccount(LinkBankAccountRequest.create({
      unitToken,
      idempotenceToken: uuid(),
      bankAccountToken
    })).then(updateBankAccountReceivedAction, updateBankAccountRejectedAction);
    return concat(of(incrementScreenAction(Screen.LOADING)), from(swapBankAccount));
  };
  return [state, work];
}
function handleUpdateBankAccountRejected(context, state, action) {
  context.sentry.captureException(action);
  return [{
    ...state
  }, () => incrementScreenAction(Screen.ERROR)];
}
function handleUpdateBankAccountReceived(context, state, action) {
  const {
    response
  } = action;
  let nextAction = incrementScreenAction(Screen.SUCCESS);
  if (response.error) {
    context.sentry.captureException(response.error);
    nextAction = incrementScreenAction(Screen.ERROR);
  }
  return [{
    ...state,
    isChangeBankAccountSuccess: response.error ? false : true
  }, () => nextAction];
}
function handleIncrementScreen(state, action) {
  const {
    screen
  } = action;
  return [{
    ...state,
    screen
  }, null];
}
function handleToggleModal(state, action) {
  const {
    unitToken,
    selectedBankLabel,
    selectedLocationName,
    bankAccountToken
  } = action;
  const {
    isModalOpen,
    isChangeBankAccountSuccess
  } = state;
  if (isModalOpen === false) {
    assert(unitToken);
    assert(selectedBankLabel);
    assert(selectedLocationName);
    assert(bankAccountToken);
    return [{
      ...state,
      isModalOpen: true,
      unitToken,
      selectedBankLabel,
      selectedLocationName,
      bankAccountToken
    }, null];
  }
  if (isChangeBankAccountSuccess) {
    return [{
      ...state,
      isModalOpen: false,
      isDone: true
    }, null];
  }
  // reset state on modal close
  return [INITIAL_STATE, null];
}
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.IncrementScreen:
      return handleIncrementScreen(state, action);
    case ActionType.ToggleChangeBankAccountModal:
      return handleToggleModal(state, action);
    case ActionType.UserClickUpdateBankAccount:
      return handleUserClickUpdateBankAccount(context, state);
    case ActionType.UpdateBankAccountRejected:
      return handleUpdateBankAccountRejected(context, state, action);
    case ActionType.UpdateBankAccountReceived:
      return handleUpdateBankAccountReceived(context, state, action);
  }
};